/**
 * Piezas compartidas del panel platform-admin de Estudios Contables.
 */
import { useMemo, useState } from 'react'
import { Search, User as UserIcon, Shield, Check, Loader2 } from 'lucide-react'
import { useSearchAdminUsers } from '@/modules/firm/hooks/useFirmAdmin'
import { usePlatformTenants } from '@/modules/platform/hooks/usePlatform'

const inputCls =
  'w-full px-3 py-2.5 text-sm rounded-lg bg-surface border border-divider-strong text-content ' +
  'placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-primary'

/**
 * Selector de usuario por correo con sugerencias en vivo (busca en el schema
 * público). El valor de salida es el email (el backend lo resuelve a User).
 */
export function UserEmailPicker({
  value,
  onChange,
  placeholder = 'correo@dominio.cl',
  autoFocus = false,
}: {
  value: string
  onChange: (email: string) => void
  placeholder?: string
  autoFocus?: boolean
}) {
  const [open, setOpen] = useState(false)
  const { data: results, isFetching } = useSearchAdminUsers(value)
  const matches = useMemo(() => results ?? [], [results])

  return (
    <div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
        <input aria-label="Buscar"
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => { onChange(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          className={inputCls.replace('px-3', 'pl-9 pr-3')}
          autoComplete="off"
        />
      </div>
      {open && value.trim().length >= 2 && (
        <div className="mt-1 w-full bg-surface border border-divider rounded-lg shadow-sm max-h-56 overflow-y-auto scrollbar-slim">
          {isFetching && matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-content-subtle">Buscando…</p>
          ) : matches.length === 0 ? (
            <p className="px-3 py-2.5 text-xs text-content-subtle">
              Sin coincidencias. El usuario debe existir; créalo primero si no aparece.
            </p>
          ) : (
            matches.map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(u.email); setOpen(false) }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-soft transition-colors"
              >
                {u.is_platform_admin
                  ? <Shield size={13} className="text-warning-solid shrink-0" />
                  : <UserIcon size={13} className="text-content-subtle shrink-0" />}
                <span className="min-w-0">
                  <span className="block text-sm text-content truncate">{u.full_name || u.email}</span>
                  <span className="block text-xs text-content-subtle truncate">{u.email}</span>
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Selector de un tenant ya aprovisionado (para vincular como cliente o como
 * empresa propia del estudio). El valor de salida es el `schema_name`.
 */
export function TenantPicker({ value, onChange }: { value: string; onChange: (schema: string, name: string) => void }) {
  const { data, isLoading } = usePlatformTenants()
  const [q, setQ] = useState('')
  const tenants = useMemo(() => {
    const list = data?.tenants ?? []
    const s = q.trim().toLowerCase()
    return s ? list.filter((t) => t.name.toLowerCase().includes(s) || t.schema_name.toLowerCase().includes(s)) : list
  }, [data, q])

  return (
    <div>
      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
        <input aria-label="Buscar empresa" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar empresa…"
          className={inputCls.replace('px-3', 'pl-9 pr-3')} />
      </div>
      <div className="border border-divider rounded-lg max-h-52 overflow-y-auto scrollbar-slim divide-y divide-divider">
        {isLoading ? (
          <p className="px-3 py-4 text-sm text-content-subtle text-center">
            <Loader2 size={16} className="animate-spin inline mr-1" /> Cargando empresas…
          </p>
        ) : tenants.length === 0 ? (
          <p className="px-3 py-4 text-sm text-content-subtle text-center">Sin empresas.</p>
        ) : (
          tenants.map((t) => (
            <button key={t.id} type="button" onClick={() => onChange(t.schema_name, t.name)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors
                ${value === t.schema_name ? 'bg-primary-50 dark:bg-primary/10' : 'hover:bg-soft'}`}>
              <span className="min-w-0">
                <span className="block text-sm text-content truncate">{t.name}</span>
                <span className="block text-xs text-content-subtle truncate">{t.schema_name}</span>
              </span>
              {value === t.schema_name && <Check size={15} className="text-primary shrink-0" />}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

export function FirmStatusBadge({ status }: { status: 'active' | 'suspended' }) {
  const cfg = status === 'active'
    ? { cls: 'bg-success-surface text-success-content', label: 'Activo' }
    : { cls: 'bg-danger-surface text-danger-content', label: 'Suspendido' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      {cfg.label}
    </span>
  )
}

export const adminInputCls = inputCls
